import { Injectable, Renderer2 } from '@angular/core';
import { Guid } from 'guid-typescript';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { firstValueFrom } from 'rxjs';
import { ModalConfirmComponent } from '../components/dynamic-elements/modal-confirm/modal-confirm.component';
import { ModalDefaultComponent } from '../components/dynamic-elements/modal-default/modal-default.component';
import { Confirm, ConfirmOption } from '../models/confirm';
import { Modal } from '../models/modal';
import { TranslationService } from '../services/translation.service';
import { uniqueId } from 'lodash';
import { NumpadComponent } from '../components/dynamic-elements/numpad/numpad.component';
import { Numpad } from '../models/numpad';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  modalBackdrop = 'bs-modal-backdrop';
  customBackdropClass = 'portal-modal';
  modalRefs: BsModalRef[] = [];
  errorModal: BsModalRef | null = null;
  constructor(private modalService: BsModalService, private translation: TranslationService) { }

  async confirm(confirm: Confirm): Promise<boolean> {
    const initialState: ModalOptions = {
      id: Guid.create().toString(),
      initialState: {
        confirm,
      },
      ignoreBackdropClick: true,
    };
    const bsModalRef = this.openModal(ModalConfirmComponent, initialState);

    if (bsModalRef?.content?.onClose) {
      return await firstValueFrom(bsModalRef.content.onClose);
    }
    return false;
  }

  async modal(modalAction: Modal, parent: EventTarget): Promise<any> {
    const initialState: ModalOptions = {
      id: Guid.create().toString(),
      initialState: {
        modalAction,
        parent,
      },
      class: modalAction.layout?.class?.join(' '),
      ignoreBackdropClick: true,
    };
    const bsModalRef = this.openModal(ModalDefaultComponent, initialState);
    if (bsModalRef?.content?.onClose) {
      return await firstValueFrom(bsModalRef.content.onClose);
    }
  }

  /** Shows a message with a single OK button and resolves when the user closes it. */
  async info(message: string, header?: string): Promise<void> {
    const confirm = new Confirm();
    confirm.header = header ?? this.translation.getTranslation('INFO');
    confirm.message = message;
    confirm.confirmOptions = [{ text: this.translation.getTranslation('OK'), value: true } as ConfirmOption];
    confirm.messageList = [];

    await this.confirm(confirm);
  }

  async error(message: string, errors?: string[]) {
    if (!this.errorModal) {
      let confirm: Confirm = new Confirm();
      confirm.header = this.translation.getTranslation('ERROR');
      confirm.message = message;
      confirm.confirmOptions = [{ text: this.translation.getTranslation('OK'), value: false } as ConfirmOption];
      confirm.messageList = errors || [];

      this.confirm(confirm).then(() => {
        this.errorModal = null;
      });
      this.errorModal = this.modalRefs[this.modalRefs.length - 1];
    } else {
      const confirm = (this.errorModal.content as ModalConfirmComponent).confirm;
      if (confirm) {
        if (confirm.message !== message) {
          confirm.message += `<br />${message}`;
          confirm.messageList.concat(errors || []);
        }
      }
    }
  }

  closeAllModals() {
    this.modalRefs.forEach((modal) => modal.hide());
  }

  async numpad(numpad: Numpad): Promise<string | null> {
    const initialState: ModalOptions = {
      initialState: {
        numpad,
      },
      class: 'modal-sm',
      ignoreBackdropClick: true,
    };
    const bsModalRef = this.openModal(NumpadComponent, initialState);
    if (bsModalRef?.content?.onClose) {
      const result = await firstValueFrom(bsModalRef.content.onClose);
      if (result != null) {
        return result as string;
      }
      return numpad.value;
    }

    return numpad.value;
  }

  openModal(content: any, options?: ModalOptions): BsModalRef {
    const modal = this.modalService.show(content, {
      id: uniqueId('modal'),
      ...options,
    });

    this.addCustomBackdrop();
    modal.onHidden!.subscribe(() => {
      this.modalRefs.pop();
      const openModals = this.modalRefs.length;
      if (openModals > 0) {
        this.addCustomBackdrop();
      } else {
        this.removeBackdrop();
      }
    });
    this.modalRefs.push(modal);
    return modal;
  }

  addCustomBackdrop() {
    let backdrop = document.querySelector(`.${this.modalBackdrop}`);
    if (!backdrop) {
      backdrop = document.createElement(this.modalBackdrop);
      backdrop.classList.add('show');
      backdrop.classList.add('modal-backdrop');
      backdrop.classList.add(this.modalBackdrop);
      document.body.appendChild(backdrop);
    }
  }

  removeBackdrop() {
    const backdrop = document.querySelectorAll(this.modalBackdrop);
    if (backdrop) {
      backdrop.forEach((b) => b.remove());
    }
  }
}
